import React, {useContext, useEffect, useRef, useCallback} from "react";
import { singletonHook } from 'react-singleton-hook';
import API from "../../../api";
import {
	prepareHardcodedOptions,
	makeAnObject,
} from "../helpers";
import useStreamActionsSingle from "../../../hooks/useStreamActionsSingle";
import AppContext, {
	STORAGE_KEY,
	initialStreamStore,
	RESPONSE_ISOLATE_ACTIVATED,
	RESPONSE_MOVIE_FINISHED,
	ISOLATED_PRODUCT, INTRO_MOVIE_FINISHED, RESPONSE_ISOLATE_DISABLED
} from "../../../store";
import Observer from "../../../helpers/Observer";
import {TABS} from "../../../contants/common";
import {formatItems} from '../../../helpers/index';
import {TIME_ACTION, TIME_DISCONNECT_SECONDS, TIME_WARNING} from "../../PopupIdle/PopupIdle";

export const webRTCObserver = new Observer();

export const EVENT_LOAD = 'EVENT_ON_LOAD';
export const EVENT_ENQUEUE_SNACKBAR = 'EVENT_ENQUEUE_SNACKBAR';
export const EVENT_UPDATE_QUEUE_DATA = 'EVENT_UPDATE_QUEUE_DATA';
export const EVENT_APPLICATION_RESPONSE = 'EVENT_APPLICATION_RESPONSE';
export const EVENT_VIDEO_FINISHED = 'EVENT_VIDEO_FINISHED';

const WEBRTC_PROPS = {
	address: process.env.REACT_APP_CONNECT_LINK,
	packageId: process.env.REACT_APP_PACKAGE_ID,
	settings: {
		audio: true, /*Audio functionality*/
		afk: {
			enabled: true,
			warnTime: TIME_WARNING,
			errorTime: 0,
			actionTime: TIME_ACTION + TIME_DISCONNECT_SECONDS * 1000,
		},
	},
	autoplay: {audio: false}, /*Audio functionality*/
};

/*@TODO Should avoid using window.scope
* I don't like it, but sometimes, you just don't have enough time
* src/store/index.js:269
* */
function handleContraSessionId() {
	if(!window?.scope?.bundles?.length) {
		console.warn('There are no Bundles')
		return;
	}

	const bundle = window.scope.bundles[0];
	window.scope.setStreamStore(initialStreamStore());
	window.scope.setActiveBundleId(bundle.bundleId);
	window.scope.setActiveTabId(TABS[0].name);
	window.scope.setSelectedOptions(bundle.options.base);
}

const initialState = {};
const useConfiguratorMountImpl = () => {
	const emitter = useStreamActionsSingle();
	const {
		bundles,
		options,
		setProducts,
		activeBundleId,
		setQueueData,
		setBundles,
		setOptions,
		setActiveBundleId,
		setSelectedOptions,
		toggleStream,
		setStreamStore,
		setSessionId,
		selectedOptions,
	} = useContext(AppContext);

	const sizeContainerRef = useRef();
	const containerRef = useRef();
	const videoRef = useRef();
	const audioRef = useRef();

	const handleLoad = function([loading, disconnected]) {
		// return false;
		const prevSessionId = JSON.parse(localStorage.getItem(STORAGE_KEY))?.sessionId;
		const sessionId = JSON.parse(localStorage.getItem('sessionId'))?.value?.session;

		if(sessionId) {
			setSessionId(sessionId);
		}

		if(!loading) {
			toggleStream(true);
		}

		if(sessionId !== prevSessionId) {
			handleContraSessionId();
		}
	};
	
	const handleUpdateQueueData = useCallback(function(res) {
		const {index, queueLength, valueType} = res; /*estimatedWaitTime*/
		console.log('handleUpdateQueueData', res)
		setQueueData({
			index,
			queueLength,
			// estimatedWaitTime: `${Math.round(estimatedWaitTime)} ${determineTimeValue(valueType, estimatedWaitTime)}`,
		})
	}, [setQueueData]);
	
	const handleApplicationResponse = useCallback(function(res) {
		// console.log('handleApplicationResponse', res)
		const { type, data = null } = makeAnObject(res);
		if(!type) return;

		switch (type) {
			case RESPONSE_ISOLATE_ACTIVATED:
			case RESPONSE_ISOLATE_DISABLED:
				setStreamStore({
					[ISOLATED_PRODUCT]: data,
				})
				return;
			case RESPONSE_MOVIE_FINISHED:
				setStreamStore({
					[INTRO_MOVIE_FINISHED]: true,
				})
				return;
			default:
				return;
		}
	}, [setStreamStore]);
	
	const subscribeAll = () => {
		webRTCObserver.subscribe(EVENT_LOAD, handleLoad);
		webRTCObserver.subscribe(EVENT_UPDATE_QUEUE_DATA, handleUpdateQueueData);
		webRTCObserver.subscribe(EVENT_APPLICATION_RESPONSE, handleApplicationResponse);
	}

	const unsubscribeAll = () => {
		webRTCObserver.unsubscribe(EVENT_LOAD, handleLoad);
		webRTCObserver.unsubscribe(EVENT_UPDATE_QUEUE_DATA, handleUpdateQueueData);
		webRTCObserver.unsubscribe(EVENT_APPLICATION_RESPONSE, handleApplicationResponse);
	};
	
	const init = useCallback(() => {
		emitter.init({
			...WEBRTC_PROPS,
			sizeContainer: sizeContainerRef.current,
			container: containerRef.current,
			videoRef: videoRef.current,
			audioRef: audioRef.current,
			playOverlay: false,
			snackbar: {
				enqueueSnackbar: function() {
					webRTCObserver.publish(EVENT_ENQUEUE_SNACKBAR, Array.from(arguments))
				},
			},
			loader: function() {
				webRTCObserver.publish(EVENT_LOAD, Array.from(arguments));
			},
			updateQueueData: function(res) {
				webRTCObserver.publish(EVENT_UPDATE_QUEUE_DATA, res);
			},
			applicationResponse: function(res) {
				webRTCObserver.publish(EVENT_APPLICATION_RESPONSE, res);
			},
		})
	}, [
		sizeContainerRef,
		containerRef,
		audioRef,
	])

	useEffect(() => {
		if(bundles.length && options.length) {
			const bundleId = activeBundleId || bundles[0].bundleId;
			const bundleActive = bundles.find(el => el.bundleId === bundleId);
			const selectedOptionsSet = Object.keys(selectedOptions || {}).length
				? Object.values(selectedOptions)
				: bundleActive
					? bundleActive.options.base.map(el => el.id)
					: bundles[0].options.base.map(el => el.id)
			setActiveBundleId(bundleId);
			setSelectedOptions(selectedOptionsSet);
		}
	}, [bundles, options, activeBundleId])

	useEffect(() => {
		subscribeAll();

		Promise.all([
			API.getBundles(),
			API.getOptionsObject(),
			API.getHardCodeOptionsObject(),
		]).then(res => {
			const [bundlesSet, optionsSet, hardcodedOptionsSet] = res;
			const preparedOptionsHardcode = prepareHardcodedOptions(hardcodedOptionsSet);

			setBundles(bundlesSet);
			setOptions([
				...optionsSet,
				...preparedOptionsHardcode,
			]);
			setProducts(formatItems([
				...bundlesSet,
				...optionsSet,
				...preparedOptionsHardcode,
			]))

			init();
		}).catch(e => console.warn("Something went wrong", e))

		return unsubscribeAll;
	}, []);

	return {
		emitter,
		sizeContainerRef,
		containerRef,
		videoRef,
		audioRef,
		onPresetClickHandler: el => emitter.emit(el.action),
	}
}

export default singletonHook(initialState, useConfiguratorMountImpl);
